import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn.js';

/// Calendário sempre aberto. O consumidor distingue dia consultado, indisponível e disponível;
/// ausência de informação nunca é apresentada como disponibilidade confirmada.
export function AvailabilityCalendar({
  month,
  today,
  maxDate,
  value,
  onMonthChange,
  onValueChange,
  availability = {},
}: {
  month: string;
  today: string;
  maxDate?: string | undefined;
  value: string | null;
  onMonthChange: (month: string) => void;
  onValueChange: (date: string) => void;
  availability?: Readonly<Record<string, 'AVAILABLE' | 'UNAVAILABLE'>>;
}) {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(year, monthNumber - 1, 1));
  const count = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const monthName = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(first);
  // Só a inicial: `capitalize` no CSS faria "Outubro De 2026".
  const label = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  function shift(step: number) {
    const date = new Date(Date.UTC(year, monthNumber - 1 + step, 1));
    onMonthChange(date.toISOString().slice(0, 7));
  }
  return (
    <div className="min-w-0 rounded-2xl border border-surface-border bg-surface-card p-3 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => shift(-1)}
          aria-label="Mês anterior"
          disabled={month <= today.slice(0, 7)}
          className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-xl border border-surface-border focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>
        <strong aria-live="polite" className="min-w-0 text-center text-xs sm:text-sm">
          {label}
        </strong>
        <button
          type="button"
          onClick={() => shift(1)}
          aria-label="Próximo mês"
          disabled={Boolean(maxDate && month >= maxDate.slice(0, 7))}
          className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-xl border border-surface-border focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1" role="group" aria-label={`Dias de ${monthName}`}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
          <span key={day} className="pb-2 text-center text-[11px] text-content-muted">
            {day}
          </span>
        ))}
        {Array.from({ length: first.getUTCDay() }, (_, index) => (
          <span key={`empty-${index}`} aria-hidden="true" />
        ))}
        {Array.from({ length: count }, (_, index) => {
          const day = index + 1;
          const date = `${month}-${String(day).padStart(2, '0')}`;
          const state = availability[date];
          return (
            <button
              key={date}
              type="button"
              disabled={date < today || Boolean(maxDate && date > maxDate)}
              onClick={() => onValueChange(date)}
              aria-pressed={date === value}
              aria-label={`${day} de ${monthName}${state === 'AVAILABLE' ? ', com horários' : state === 'UNAVAILABLE' ? ', sem horários' : ', consulte os horários'}`}
              className={cn(
                'flex min-h-11 min-w-0 cursor-pointer flex-col items-center justify-center rounded-lg border border-transparent text-sm tabular-nums transition-colors hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-30',
                date === value && 'border-brand-500 bg-brand-500/15 font-bold text-brand-600',
              )}
            >
              <span>{day}</span>
              <span
                aria-hidden="true"
                className={cn(
                  'mt-1 size-1 rounded-full',
                  state === 'AVAILABLE' ? 'bg-brand-500' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-content-muted">
        Ponto dourado: dia com horários. Selecione outro dia para consultar.
      </p>
    </div>
  );
}
